import { HttpTypes } from "@medusajs/types";

/**
 * Get currency symbol for display
 */
export const getSymbol = (currency: string): string => {
  switch (currency.toUpperCase()) {
    case "EUR":
      return "€";
    case "USD":
      return "$";
    case "CAD":
      return "$"; // Listed as CUP in UI
    default:
      return "$";
  }
};

/**
 * Get the price for a specific currency from a product variant's prices array.
 * Falls back to the calculated_price if no exact match is found.
 */
export const getPriceForCurrency = (
  variant: HttpTypes.StoreProductVariant,
  currencyCode: string
): number => {
  const upperCurrency = currencyCode.toUpperCase();

  // First try to find exact currency match in prices array
  const price = (variant as any).prices?.find(
    (p: any) => p.currency_code?.toUpperCase() === upperCurrency
  );

  if (price?.amount !== undefined) {
    return price.amount;
  }

  // Fallback to calculated_price if available
  if (variant.calculated_price?.calculated_amount !== undefined) {
    return variant.calculated_price.calculated_amount ?? 0;
  }

  // Return 0 if no price found
  return 0;
};

/**
 * Check if a variant has a price in the specified currency
 */
export const hasPriceForCurrency = (
  variant: HttpTypes.StoreProductVariant,
  currencyCode: string
): boolean => {
  const upperCurrency = currencyCode.toUpperCase();
  return (variant as any).prices?.some(
    (p: any) => p.currency_code?.toUpperCase() === upperCurrency
  ) ?? false;
};

/**
 * Get all available currencies for a variant
 */
export const getAvailableCurrencies = (
  variant: HttpTypes.StoreProductVariant
): string[] => {
  const currencies = new Set<string>();
  (variant as any).prices?.forEach((p: any) => {
    if (p.currency_code) {
      currencies.add(p.currency_code.toUpperCase());
    }
  });
  return Array.from(currencies);
};

/**
 * Format a price with the appropriate currency symbol
 */
export const formatPriceWithCurrency = (
  amount: number,
  currencyCode: string
): string => {
  const code = currencyCode.toUpperCase();
  
  // Handle special case: CAD is displayed as CUP for Cuba
  const displayCode = code === "CAD" ? "CUP" : code;

  try {
    // For CUP (Cuban Peso), use simple formatting
    if (displayCode === "CUP") {
      return `CUP ${amount.toFixed(2)}`;
    }

    // Use Intl.NumberFormat for other currencies
    return new Intl.NumberFormat("es-CU", {
      style: "currency",
      currency: code === "CAD" ? "USD" : code, // Use USD as base for CAD to avoid Intl errors
    }).format(amount).replace("USD", displayCode).replace("US$", "$");
  } catch {
    // Fallback formatting
    return `${displayCode} ${amount.toFixed(2)}`;
  }
};

/**
 * Get the price object for a specific currency
 */
export const getPriceObjectForCurrency = (
  variant: HttpTypes.StoreProductVariant,
  currencyCode: string
): any | undefined => {
  const upperCurrency = currencyCode.toUpperCase();
  return (variant as any).prices?.find(
    (p: any) => p.currency_code?.toUpperCase() === upperCurrency
  );
};

/**
 * Compare prices across currencies for a variant
 * Returns the best price (lowest) and its currency
 */
export const getBestPrice = (
  variant: HttpTypes.StoreProductVariant
): { amount: number; currencyCode: string } | null => {
  if (!(variant as any).prices?.length) return null;

  let bestPrice = (variant as any).prices[0];
  for (const price of (variant as any).prices) {
    if ((price.amount ?? Infinity) < (bestPrice.amount ?? Infinity)) {
      bestPrice = price;
    }
  }

  return {
    amount: bestPrice.amount ?? 0,
    currencyCode: bestPrice.currency_code?.toUpperCase() ?? "USD",
  };
};
